/**
 * C3 v3 funnel vocabulary and conservation checks.
 * Maps mock "incident" language onto the real backend units.
 */

export const FUNNEL_WINDOWS = ['24h', '48h', '72h', '7d', '1m', '3m', '1y', 'all_time'] as const;
export type FunnelWindow = (typeof FUNNEL_WINDOWS)[number];
export const DEFAULT_FUNNEL_WINDOW: FunnelWindow = '7d';
/** @deprecated use DEFAULT_FUNNEL_WINDOW */
export const FUNNEL_WINDOW = DEFAULT_FUNNEL_WINDOW;

const DAY_MS = 24 * 60 * 60 * 1000;
export const FUNNEL_WINDOW_MS: Record<Exclude<FunnelWindow, 'all_time'>, number> = {
  '24h': DAY_MS,
  '48h': 2 * DAY_MS,
  '72h': 3 * DAY_MS,
  '7d': 7 * DAY_MS,
  '1m': 30 * DAY_MS,
  '3m': 90 * DAY_MS,
  '1y': 365 * DAY_MS,
};

export const FUNNEL_WINDOW_LABELS: Record<FunnelWindow, string> = {
  '24h': '24h',
  '48h': '48h',
  '72h': '72h',
  '7d': '7 Days',
  '1m': '1 Month',
  '3m': '3 Months',
  '1y': '1 Year',
  all_time: 'All time',
};

export function funnelWindowMs(window: FunnelWindow): number | null {
  if (window === 'all_time') return null;
  return FUNNEL_WINDOW_MS[window];
}

export function funnelWindowLabel(window: FunnelWindow): string {
  return FUNNEL_WINDOW_LABELS[window];
}

/**
 * The periods the Overview and the feed's Time filter offer (client notes 10/26, item 8): 24 hours by default, then
 * 48 and 72 hours, 7 and 30 days. "1m" is the 30 days of the longer windows.
 */
export type PeriodWindow = '24h' | '48h' | '72h' | '7d' | '1m';
export const PERIOD_WINDOWS: readonly PeriodWindow[] = ['24h', '48h', '72h', '7d', '1m'];
export const DEFAULT_PERIOD: PeriodWindow = '24h';

/** A window as a span of time ("24 hours"), and as the short tag a tile carries ("24h"). */
const WINDOW_SPAN: Record<FunnelWindow, string> = {
  '24h': '24 hours',
  '48h': '48 hours',
  '72h': '72 hours',
  '7d': '7 days',
  '1m': '30 days',
  '3m': '90 days',
  '1y': '1 year',
  all_time: 'all time',
};
const WINDOW_SHORT: Record<FunnelWindow, string> = {
  '24h': '24h',
  '48h': '48h',
  '72h': '72h',
  '7d': '7d',
  '1m': '30d',
  '3m': '90d',
  '1y': '1y',
  all_time: 'All',
};

export function windowSpan(window: FunnelWindow): string {
  return WINDOW_SPAN[window];
}

export function windowShort(window: FunnelWindow): string {
  return WINDOW_SHORT[window];
}

/** "in the last 24 hours"; "on record" for all time. */
export function inLast(window: FunnelWindow): string {
  return window === 'all_time' ? 'on record' : `in the last ${WINDOW_SPAN[window]}`;
}

export function isFunnelWindow(value: string): value is FunnelWindow {
  return (FUNNEL_WINDOWS as readonly string[]).includes(value);
}

export const FUNNEL_LABELS = {
  alert: 'security event',
  threat: 'threat',
  incident: 'threat',
  campaign: 'campaign',
  standalone: 'standalone threat',
  action: 'action',
  candidate: 'candidate',
} as const;

export interface FunnelCandidateCounts {
  registered: number;
  pending: number;
  analyzing: number;
  error: number;
  duplicated: number;
  unique: number;
}

export interface FunnelEventCounts {
  total: number;
  new: number;
  duplicate: number;
}

export interface FunnelVerdictCounts {
  baseline: number;
  suppressed: number;
  escalated: number;
  threat: number;
}

export interface FunnelThreatCounts {
  total: number;
  active: number;
  inProgress: number;
  remediated: number;
  folded: number;
  inCampaign: number;
  standalone: number;
  /** Point-in-time open incidents (active + in_progress + reopened). */
  open: number;
  byRisk: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}

export interface FunnelCampaignCounts {
  total: number;
  open: number;
  closed: number;
}

export interface FunnelActionCounts {
  total: number;
  pending: number;
  confirmed: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  reverted: number;
  auto: number;
  gated: number;
}

export interface FunnelClockStat {
  medianMs: number;
  p90Ms: number;
  n: number;
}

export interface FunnelQueueCounts {
  waiting: number;
  oldestWaitMs: number | null;
}

export interface FunnelCompressionCounts {
  alerts: number;
  incidents: number;
  decisions: number;
}

export interface FunnelFpCounts {
  suppressed: number;
  anomalies: number;
  threats: number;
}

export interface FunnelAutoResolution {
  autoCompleted: number;
  closed: number;
  gatedCompleted: number;
}

export interface FunnelRollbackCounts {
  reverted: number;
  executed: number;
  validationFailed: number;
  humanRollback: number;
}

export interface FunnelIntegrations {
  healthy: number;
  connected: number;
  worstLagMs: number | null;
}

export interface FunnelClocks {
  assign: FunnelClockStat | null;
  triage: FunnelClockStat | null;
  remediate: FunnelClockStat | null;
  resolve: FunnelClockStat | null;
}

/**
 * The same headline figures for the previous period of the same length, when the backend sends them
 * (docs/c3/BACKEND.md, item 59). Each field is the figure as the band shows it: counts as counts,
 * durations in milliseconds, rates as percentages (0-100), compression as alerts per decision.
 */
export interface FunnelPrevious {
  alerts?: number;
  compression?: number;
  assignMs?: number;
  triageMs?: number;
  remediateMs?: number;
  resolveMs?: number;
  suppressed?: number;
  autoResolutionPct?: number;
  rollbackPct?: number;
}

export interface FunnelCounters {
  window: FunnelWindow;
  asOf: string;
  /** Absent until the backend sends the previous period; the band then shows no change. */
  previous?: FunnelPrevious;
  candidates: FunnelCandidateCounts;
  events: FunnelEventCounts;
  verdicts: FunnelVerdictCounts;
  threats: FunnelThreatCounts;
  campaigns: FunnelCampaignCounts;
  actions: FunnelActionCounts;
  queue: FunnelQueueCounts;
  compression: FunnelCompressionCounts;
  fp: FunnelFpCounts;
  autoResolution: FunnelAutoResolution;
  rollback: FunnelRollbackCounts;
  integrations: FunnelIntegrations;
  clocks: FunnelClocks;
}

export interface ConservationCheck {
  ok: boolean;
  expected: number;
  actual: number;
  diagnosis: string;
}

export interface FunnelConservation {
  candidates: ConservationCheck;
  verdicts: ConservationCheck;
  threats: ConservationCheck;
  allOk: boolean;
}

function check(actual: number, expected: number, diagnosis: string): ConservationCheck {
  return { ok: actual === expected, expected, actual, diagnosis };
}

export function emptyFunnelCounters(
  asOf = new Date().toISOString(),
  window: FunnelWindow = DEFAULT_FUNNEL_WINDOW
): FunnelCounters {
  return {
    window,
    asOf,
    candidates: { registered: 0, pending: 0, analyzing: 0, error: 0, duplicated: 0, unique: 0 },
    events: { total: 0, new: 0, duplicate: 0 },
    verdicts: { baseline: 0, suppressed: 0, escalated: 0, threat: 0 },
    threats: {
      total: 0,
      active: 0,
      inProgress: 0,
      remediated: 0,
      folded: 0,
      inCampaign: 0,
      standalone: 0,
      open: 0,
      byRisk: { critical: 0, high: 0, medium: 0, low: 0 },
    },
    campaigns: { total: 0, open: 0, closed: 0 },
    actions: {
      total: 0,
      pending: 0,
      confirmed: 0,
      inProgress: 0,
      completed: 0,
      cancelled: 0,
      reverted: 0,
      auto: 0,
      gated: 0,
    },
    queue: { waiting: 0, oldestWaitMs: null },
    compression: { alerts: 0, incidents: 0, decisions: 0 },
    fp: { suppressed: 0, anomalies: 0, threats: 0 },
    autoResolution: { autoCompleted: 0, closed: 0, gatedCompleted: 0 },
    rollback: { reverted: 0, executed: 0, validationFailed: 0, humanRollback: 0 },
    integrations: { healthy: 0, connected: 0, worstLagMs: null },
    clocks: { assign: null, triage: null, remediate: null, resolve: null },
  };
}

export function checkFunnelConservation(c: FunnelCounters): FunnelConservation {
  const candActual =
    c.candidates.pending +
    c.candidates.analyzing +
    c.candidates.error +
    c.candidates.duplicated +
    c.candidates.unique;
  const verdActual =
    c.verdicts.baseline + c.verdicts.suppressed + c.verdicts.escalated + c.verdicts.threat;
  const threatActual = c.threats.inCampaign + c.threats.standalone;

  const candidates = check(
    candActual,
    c.candidates.registered,
    `terminals ${candActual} ≠ registered ${c.candidates.registered}`
  );
  const verdicts = check(
    verdActual,
    c.candidates.unique,
    `verdicts ${verdActual} ≠ unique ${c.candidates.unique}`
  );
  const threats = check(
    threatActual,
    c.threats.total,
    `inCampaign + standalone ${threatActual} ≠ threats ${c.threats.total}`
  );

  return {
    candidates,
    verdicts,
    threats,
    allOk: candidates.ok && verdicts.ok && threats.ok,
  };
}

export function isWithinFunnelWindow(
  iso: string | undefined,
  nowMs = Date.now(),
  window: FunnelWindow = DEFAULT_FUNNEL_WINDOW
): boolean {
  if (window === 'all_time') return true;
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return false;
  const maxAge = FUNNEL_WINDOW_MS[window];
  const age = nowMs - ms;
  return age <= maxAge && age >= 0;
}

/** Whether a remediation plan was handed to a higher level of management and has not been decided since (the Escalated state). */
export function isEscalatedPlan(plan: { status?: unknown; escalated_at?: unknown; decided_at?: unknown } | null | undefined): boolean {
  if (!plan) return false;
  if (String(plan.status ?? '').trim().toLowerCase() === 'escalated') return true;
  return Boolean(String(plan.escalated_at ?? '').trim()) && !String(plan.decided_at ?? '').trim();
}

export function sourceRefId(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['value', '_id', 'id'] as const) {
      const nested = sourceRefId(obj[key]);
      if (nested) return nested;
    }
  }
  return '';
}

export function flattenIdList(raw: unknown): string[] {
  const out: string[] = [];
  const walk = (value: unknown) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    const id = sourceRefId(value);
    if (id) out.push(id);
  };
  walk(raw);
  return out;
}

export function linkTargetId(link: unknown): string {
  if (!link || typeof link !== 'object') return sourceRefId(link);
  const obj = link as Record<string, unknown>;
  return sourceRefId(obj.value ?? obj._id ?? obj.id);
}

export function linkHasLabel(link: unknown, forward: string): boolean {
  if (!link || typeof link !== 'object') return false;
  const labels = (link as Record<string, unknown>).label;
  if (Array.isArray(labels)) {
    return labels.map((x) => String(x).toUpperCase()).includes(forward.toUpperCase());
  }
  return String(labels || '').toUpperCase() === forward.toUpperCase();
}

export function campaignMemberActionIds(campaign: { links?: unknown }): string[] {
  const links = Array.isArray(campaign.links) ? campaign.links : [];
  return links
    .filter((link) => linkHasLabel(link, 'CONTAINS'))
    .map(linkTargetId)
    .filter(Boolean);
}

export function normalizeRiskBand(value: unknown): keyof FunnelThreatCounts['byRisk'] {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (raw === 'critical') return 'critical';
  if (raw === 'high') return 'high';
  if (raw === 'medium' || raw === 'med') return 'medium';
  return 'low';
}

export function percentileMs(sortedAsc: number[], p: number): number {
  if (sortedAsc.length === 0) return 0;
  if (sortedAsc.length === 1) return sortedAsc[0];
  const rank = Math.min(sortedAsc.length - 1, Math.max(0, Math.ceil((p / 100) * sortedAsc.length) - 1));
  return sortedAsc[rank];
}

export function clockStatFromDurations(durationsMs: number[]): FunnelClockStat | null {
  const clean = durationsMs.filter((n) => Number.isFinite(n) && n >= 0).sort((a, b) => a - b);
  if (clean.length === 0) return null;
  return {
    medianMs: percentileMs(clean, 50),
    p90Ms: percentileMs(clean, 90),
    n: clean.length,
  };
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  // No trailing zero part: "1m", not "1m 00s".
  if (mins < 60) return secs === 0 ? `${mins}m` : `${mins}m ${String(secs).padStart(2, '0')}s`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins === 0 ? `${hours}h` : `${hours}h ${String(remMins).padStart(2, '0')}m`;
}

export function remediationTime(rem: {
  verified_at?: string;
  decided_at?: string;
  assigned_at?: string;
  formed_at?: string;
}): string {
  return rem.verified_at || rem.decided_at || rem.assigned_at || rem.formed_at || '';
}
